// src/lib/sat/save-status.ts
//
// Whether each question's answer has actually reached the server.
//
// The save path queues every answer until the server confirms it (#820, #2017),
// but the test only ever showed one line for the whole module — and that line
// read "Reconnecting — saving your work…" for every ordinary in-flight save,
// because a save joins the queue before its request goes out. A student could
// not tell a healthy save from a failed one, or which question was affected
// (audit 2026-09-11: "add clear saved/pending/error feedback").
//
// Three states per question, and only two need recording:
//   saving  — a request is in flight and nothing has failed yet;
//   unsaved — a save attempt failed (or was left over from a previous page
//             load) and will be retried; this is the one that needs attention;
//   saved   — no entry: the server has the latest value.
// A retry of an unsaved answer stays "unsaved" until it lands: showing
// "saving" again would hide the fact that it has already failed once.

export type SaveStatus = 'saved' | 'saving' | 'unsaved'

/** questionId → status, for questions that are not saved. */
export type SaveStates = Readonly<Record<string, Exclude<SaveStatus, 'saved'>>>

export function markSaving(states: SaveStates, questionId: string): SaveStates {
  if (states[questionId] === 'unsaved' || states[questionId] === 'saving') return states
  return { ...states, [questionId]: 'saving' }
}

/**
 * Record how a save ended. `isLatest` is false when a newer body for the same
 * question was queued after this one: this save's outcome — success or
 * failure — says nothing about the newer value, so it changes nothing. (An
 * older save failing after a newer one landed must not mark the question
 * unsaved.)
 */
export function markSettled(
  states: SaveStates,
  questionId: string,
  ok: boolean,
  isLatest: boolean,
): SaveStates {
  if (!isLatest) return states
  if (!ok) {
    return states[questionId] === 'unsaved' ? states : { ...states, [questionId]: 'unsaved' }
  }
  if (!(questionId in states)) return states
  const next = { ...states }
  delete next[questionId]
  return next
}

/** Answers recovered from a previous page load never reached the server. */
export function markRecovered(states: SaveStates, questionIds: string[]): SaveStates {
  if (questionIds.length === 0) return states
  const next = { ...states }
  for (const id of questionIds) next[id] = 'unsaved'
  return next
}

export function statusOf(states: SaveStates, questionId: string): SaveStatus {
  return states[questionId] ?? 'saved'
}

export function unsavedCount(states: SaveStates): number {
  return Object.values(states).filter((s) => s === 'unsaved').length
}

/** The status line under the current question. Empty when there is nothing to say. */
export function saveStatusLine(
  states: SaveStates,
  currentQuestionId: string | null,
  currentAnswered: boolean,
): { tone: 'warn' | 'muted' | 'none'; text: string } {
  const unsaved = unsavedCount(states)
  const current = currentQuestionId ? statusOf(states, currentQuestionId) : 'saved'
  if (current === 'unsaved') {
    const others = unsaved - 1
    return {
      tone: 'warn',
      text:
        "This answer isn't saved yet. Retrying — keep this page open." +
        (others > 0 ? ` (${others} other${others === 1 ? '' : 's'} also waiting.)` : ''),
    }
  }
  if (unsaved > 0) {
    return {
      tone: 'warn',
      text: `${unsaved === 1 ? "1 answer isn't" : `${unsaved} answers aren't`} saved yet. Retrying — keep this page open.`,
    }
  }
  if (current === 'saving') return { tone: 'muted', text: 'Saving…' }
  if (currentAnswered) return { tone: 'muted', text: 'Saved' }
  return { tone: 'none', text: '' }
}
